import { Box, Flex, Text, Heading, Card, VStack, SimpleGrid } from "@chakra-ui/react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { AppButton, AppInput, AppModal } from "../../components";
import { colors } from "../../theme";
import { setLoginUser, logoutUser } from "../../redux/reducer/userSlice";
import { showToast } from "../../redux/reducer/utilsSlice";
import { changePassword, logoutAdmin, updateAdminProfile } from "../../services/apiCalls";
import { useNavigate } from "react-router-dom";

const SettingsScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.user);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: loginUser?.name || "",
      email: loginUser?.email || "",
      phone: loginUser?.phoneNumber || "",
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
    watch,
  } = useForm();

  // Saves name and phone to the server (email is changed through support).
  // Failures are shown by the request layer's error toast.
  const onProfileSubmit = async (data) => {
    const [firstName, ...rest] = (data.name || "").trim().split(/\s+/);
    try {
      await updateAdminProfile({
        firstName,
        lastName: rest.join(" ") || undefined,
        phoneNumber: data.phone || undefined,
      });
      dispatch(setLoginUser({ ...loginUser, name: data.name, phoneNumber: data.phone }));
      dispatch(showToast({ message: "Profile saved", type: "success" }));
    } catch (e) {
      // error toast already shown
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      await changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      dispatch(showToast({ message: "Password changed", type: "success" }));
      setIsPasswordModalOpen(false);
      resetPassword();
    } catch (e) {
      // error toast already shown; keep the dialog open to retry
    }
  };

  // End the session on the server too, so the old token stops working.
  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } catch (e) {
      // sign out locally regardless
    }
    localStorage.removeItem("token");
    dispatch(logoutUser());
    navigate("/login");
  };

  return (
    <Box>
      {/* Page Header */}
      <Box mb={6}>
        <Heading size="lg" color={colors.text} mb={1}>
          Settings
        </Heading>
        <Text color={colors.textSecondary}>
          Manage your account settings and preferences
        </Text>
      </Box>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Profile Settings */}
        <Card.Root bg="white" borderRadius="lg" boxShadow="sm">
          <Card.Header p={6} pb={4}>
            <Heading size="md" color={colors.text}>
              Profile Settings
            </Heading>
          </Card.Header>
          <Card.Body px={6} pb={6} pt={0}>
            <form onSubmit={handleSubmit(onProfileSubmit)}>
              <VStack spacing={4} align="stretch">
                <AppInput
                  label="Full Name"
                  name="name"
                  placeholder="Enter your name"
                  register={register}
                  errors={errors}
                  required
                />
                <AppInput
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  register={register}
                  errors={errors}
                  disabled
                />
                <AppInput
                  label="Phone Number"
                  name="phone"
                  placeholder="Enter your phone number"
                  register={register}
                  errors={errors}
                />
                <AppButton type="submit" variant="primary" alignSelf="flex-start">
                  Save Changes
                </AppButton>
              </VStack>
            </form>
          </Card.Body>
        </Card.Root>

        {/* Security Settings */}
        <Card.Root bg="white" borderRadius="lg" boxShadow="sm">
          <Card.Header p={6} pb={4}>
            <Heading size="md" color={colors.text}>
              Security
            </Heading>
          </Card.Header>
          <Card.Body px={6} pb={6} pt={0}>
            <VStack spacing={4} align="stretch">
              <Box
                p={4}
                bg={colors.background}
                borderRadius="md"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Text fontWeight="500" color={colors.text}>
                    Password
                  </Text>
                  <Text fontSize="sm" color={colors.textSecondary}>
                    Change your account password
                  </Text>
                </Box>
                <AppButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsPasswordModalOpen(true)}
                >
                  Change Password
                </AppButton>
              </Box>

              <Box
                p={4}
                bg={colors.background}
                borderRadius="md"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Text fontWeight="500" color={colors.text}>
                    Two-Factor Authentication
                  </Text>
                  <Text fontSize="sm" color={colors.textSecondary}>
                    Add an extra layer of security
                  </Text>
                </Box>
                <AppButton variant="secondary" size="sm" disabled>
                  Coming Soon
                </AppButton>
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* Account Actions */}
        <Card.Root bg="white" borderRadius="lg" boxShadow="sm">
          <Card.Header p={6} pb={4}>
            <Heading size="md" color={colors.text}>
              Account
            </Heading>
          </Card.Header>
          <Card.Body px={6} pb={6} pt={0}>
            <VStack spacing={4} align="stretch">
              <Box
                p={4}
                bg={colors.background}
                borderRadius="md"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Text fontWeight="500" color={colors.text}>
                    Sign Out
                  </Text>
                  <Text fontSize="sm" color={colors.textSecondary}>
                    Sign out of your account
                  </Text>
                </Box>
                <AppButton variant="danger" size="sm" onClick={handleLogout}>
                  Sign Out
                </AppButton>
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* App Info */}
        <Card.Root bg="white" borderRadius="lg" boxShadow="sm">
          <Card.Header p={6} pb={4}>
            <Heading size="md" color={colors.text}>
              About
            </Heading>
          </Card.Header>
          <Card.Body px={6} pb={6} pt={0}>
            <VStack spacing={3} align="stretch">
              <Flex justify="space-between">
                <Text color={colors.textSecondary}>Version</Text>
                <Text fontWeight="500">1.0.0</Text>
              </Flex>
              <Flex justify="space-between">
                <Text color={colors.textSecondary}>Environment</Text>
                <Text fontWeight="500">
                  {process.env.NODE_ENV === "production" ? "Production" : "Development"}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text color={colors.textSecondary}>Last Updated</Text>
                <Text fontWeight="500">December 30, 2024</Text>
              </Flex>
            </VStack>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>

      {/* Password Change Modal */}
      <AppModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="Change Password"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsPasswordModalOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              onClick={handlePasswordSubmit(onPasswordSubmit)}
            >
              Update Password
            </AppButton>
          </Flex>
        }
      >
        <VStack spacing={4}>
          <AppInput
            label="Current Password"
            name="currentPassword"
            type="password"
            placeholder="Enter current password"
            register={registerPassword}
            errors={passwordErrors}
            required
          />
          <AppInput
            label="New Password"
            name="newPassword"
            type="password"
            placeholder="Enter new password"
            register={registerPassword}
            errors={passwordErrors}
            required
          />
          <AppInput
            label="Confirm New Password"
            name="confirmPassword"
            type="password"
            placeholder="Confirm new password"
            register={registerPassword}
            errors={passwordErrors}
            required
          />
        </VStack>
      </AppModal>
    </Box>
  );
};

export default SettingsScreen;
